import React, { useState } from "react";
import {
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Slide,
  Dialog,
  Box,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Cancel, ErrorOutline } from "@mui/icons-material";
import { TransitionProps } from "@mui/material/transitions";
import JSONPretty from "react-json-pretty";
import { useModalStyles } from "../../common/useModalStyles";

interface CustomCommentPopupProps {
  title?: string;
  response?: any;
  open: boolean;
  onClose: () => void;
  canViewErrorResponse?: boolean;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const BenefitVerificationErrorResponse: React.FC<CustomCommentPopupProps> = ({
  title,
  response,
  open,
  onClose,
  canViewErrorResponse = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDark, tokens, paperSx, headerSx, iconBoxSx, closeIconSx, bodySx, footerSx, cancelBtnSx } = useModalStyles();
  const handleClose = () => {
    onClose();
  };
  const formattedJson = JSON.stringify(response,null,2);
  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        keepMounted
        PaperProps={{ sx: { ...paperSx(isMobile), width: isMobile ? "100%" : "560px" } }}
      >
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <Box sx={headerSx}>
            <Box sx={iconBoxSx(tokens.error)}>
              <ErrorOutline sx={{ fontSize: "1.2rem", color: tokens.error }} />
            </Box>
            <Box sx={{ flex: 1, pr: 4 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}>
                {title}
              </Typography>
              <Typography id="alert-dialog-slide-description" sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
                Eligibility check API error details.
              </Typography>
            </Box>
            <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
              <CloseIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Box>
          <Box
            sx={{
              ...bodySx,
              minHeight: 0,
              overflow: "hidden",
            }}
          >
            {response && response?.errolog && (<>
              <Typography sx={{ color: tokens.error, fontSize: "1rem", fontWeight: 700 }}>
                {response?.errolog}
              </Typography>
            </>)}
            {canViewErrorResponse && (
            <Box sx={{
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              overflowY: "auto",
              overflowX: "auto",
              maxHeight: { xs: "45vh", sm: "52vh" },
              p: 1.25,
              backgroundColor: tokens.cardBg,
              borderRadius: "10px",
              color: tokens.textPrimary,
              border: `1px solid ${tokens.border}`,
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                background: isDark ? "rgba(255,255,255,0.12)" : "#CBD5E1",
                borderRadius: 4,
              },
            }}>
              <div
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                  cursor: "pointer",
                  fontWeight: "bold",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "6px" }}>{isExpanded ? "−" : "+"}</span>
                <span>{isExpanded ? "Hide error response" : "Show error response"}</span>
              </div>

              {isExpanded && (
                //CS-1170 - START
                <JSONPretty data={response?.errorResponse} style={{ fontSize: "1em" }} />
                //CS-1170 - END
              )}
            </Box>
            )}
          </Box>
          <Box sx={footerSx}>
            <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
              {/* CS-1404 - Cancel button style change. */}
              <Button
                onClick={handleClose}
                startIcon={<Cancel/>}
                variant="outlined"
                sx={{
                  ...cancelBtnSx,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.8,
                  px: 3,
                  py: 1.2,
                  border: "1px solid",
                  cursor: "pointer",
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default BenefitVerificationErrorResponse;
